// Add App User. POST /v1/admin/app-users.
//
// Password is auto-generated server-side and returned ONCE in the response.
// Same one-time-reveal pattern as AdminUserNew.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { useToast } from "@/components/Toast";

interface CreateRes {
  ok: boolean;
  id: string;
  generated_password: string | null;
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "username_taken":
      return "That user name is already taken.";
    case "create_failed":
      return "Couldn't create the account. The email or phone may already be in use.";
    case "bad_request":
      return "Invalid input. Double-check the form and try again — phone must be in E.164 format (e.g. +85255551234).";
    default:
      return err.message || fallback;
  }
}

export function AppUserNew() {
  const navigate = useNavigate();
  const { show, ToastEl } = useToast();
  const [form, setForm] = useState({
    role: "customer" as "customer" | "engineer",
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
  });
  const [reveal, setReveal] = useState<{ id: string; password: string } | null>(
    null,
  );

  const create = useMutation({
    mutationFn: () =>
      apiFetch<CreateRes>("/v1/admin/app-users", {
        method: "POST",
        body: {
          role: form.role,
          username: form.username,
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          // Phone is now required by CreateAppUserReq — the mobile app
          // uses it as the login identifier.
          phone: form.phone.trim(),
        },
      }),
    onSuccess: (res) => {
      if (res.generated_password) {
        setReveal({ id: res.id, password: res.generated_password });
      } else {
        show("App user created", "success");
        navigate(`/app/app-users/${res.id}`);
      }
    },
    onError: (err) => show(friendlyError(err, "Create failed"), "error"),
  });

  // Phone is now mandatory (mobile app's login id). E.164 required.
  const phoneOk = /^\+\d{7,15}$/.test(form.phone.trim());
  const valid =
    Boolean(form.email) &&
    Boolean(form.first_name) &&
    Boolean(form.last_name) &&
    /^[A-Za-z0-9._-]{3,50}$/.test(form.username) &&
    phoneOk;

  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
  };

  const copyPassword = async () => {
    if (!reveal) return;
    try {
      await navigator.clipboard.writeText(reveal.password);
      show("Password copied to clipboard", "success");
    } catch {
      show("Couldn't access clipboard — copy manually", "warning");
    }
  };

  const onContinue = () => {
    if (!reveal) return;
    const id = reveal.id;
    setReveal(null);
    navigate(`/app/app-users/${id}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          <TextField
            variant="standard"
            fullWidth
            required
            select
            label="Role"
            value={form.role}
            onChange={(e) =>
              setForm({ ...form, role: e.target.value as "customer" | "engineer" })
            }
          >
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="engineer">Engineer</MenuItem>
          </TextField>
          <TextField
            variant="standard"
            fullWidth
            required
            label="User Name"
            helperText="3-50 chars; letters, numbers, . _ -"
            inputProps={{ maxLength: 50 }}
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
          />
          <TextField
            variant="standard"
            fullWidth
            required
            label="E-mail"
            type="email"
            inputProps={{ maxLength: 254 }}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <TextField
            variant="standard"
            fullWidth
            required
            label="Phone"
            error={form.phone.length > 0 && !phoneOk}
            helperText={
              form.phone.length > 0 && !phoneOk
                ? "Use E.164 format e.g. +85255551234"
                : "Required · E.164 format e.g. +85255551234 (the mobile login id)"
            }
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          <TextField
            variant="standard"
            fullWidth
            required
            label="First Name"
            inputProps={{ maxLength: 80 }}
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          />
          <TextField
            variant="standard"
            fullWidth
            required
            label="Last Name"
            inputProps={{ maxLength: 80 }}
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          />
        </Box>
        <Typography
          sx={{
            mb: 2,
            color: tokens.colors.admin.textBody,
            fontSize: tokens.font.sizes.bodySm,
          }}
        >
          A strong password will be generated automatically and shown to you
          once after the account is created. Copy it and share it securely
          with the new user — it cannot be retrieved later.
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            disabled={!valid || create.isPending}
            sx={primaryBtn}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            onClick={() => navigate("/app/app-users")}
          >
            Cancel
          </Button>
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(reveal)}
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          onContinue();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>One-time password</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            The new user's password is shown below. Copy it now and send it
            to them through a secure channel — once you close this dialog
            it cannot be retrieved.
          </Typography>
          <TextField
            fullWidth
            value={reveal?.password ?? ""}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: "monospace" },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy">
                    <IconButton onClick={copyPassword} aria-label="Copy password">
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" sx={primaryBtn} onClick={onContinue}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
      {ToastEl}
    </Box>
  );
}
